import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  type FormControlProps,
  Tag,
  Box,
} from '@chakra-ui/react';
import { Select as ChakraReactSelect, ChakraStylesConfig } from 'chakra-react-select';
import { useCallback } from 'react';
import type { FormFieldOption } from '@duke-hq/shared-constants/assessments';
import { testIds } from './testIds';

interface DropdownSelectFieldProps extends Omit<FormControlProps, 'onChange' | 'value'> {
  id: string;
  label: string;
  options: FormFieldOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  error?: string;
  isCorrect?: boolean | null;
}

const defaultStyles: ChakraStylesConfig = {
  container: (base) => ({
    ...base,
    w: 'full',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    bg: 'transparent',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none',
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

const disabledStyles: ChakraStylesConfig = {
  ...defaultStyles,
  downChevron: (base) => ({
    ...base,
    color: 'fg.disabled',
  }),
  container: (base) => ({
    ...base,
    color: 'fg.muted',
    w: 'full',
  }),
};

/**
 * Renders a chakra-react-select dropdown within a form control.
 */
export function DropdownSelectField({
  id,
  label,
  options,
  value,
  onChange,
  placeholder,
  helperText,
  error,
  isCorrect,
  ...formControlProps
}: DropdownSelectFieldProps) {
  const isInvalid = error !== undefined && error.length > 0;
  const selectedOption = options.find((opt) => opt.value === value) ?? null;
  const handleChange = useCallback(
    (selectedOption: unknown) => {
      if (
        selectedOption != null &&
        typeof selectedOption === 'object' &&
        'value' in selectedOption &&
        selectedOption.value != null &&
        typeof selectedOption.value === 'string'
      ) {
        onChange(selectedOption.value);
      }
    },
    [onChange],
  );

  return (
    <FormControl isInvalid={isInvalid} {...formControlProps} sx={{ display: 'contents' }}>
      <FormLabel htmlFor={id} fontWeight="semibold">
        {typeof isCorrect === 'boolean' && (
          <Tag size="sm" variant="subtle" colorScheme={isCorrect ? 'green' : 'red'} mr={2}>
            {isCorrect ? 'Correct' : 'Incorrect'}
          </Tag>
        )}
        {label}
      </FormLabel>

      <Box gridRow="2" minH={0}>
        {helperText !== undefined && helperText.length > 0 ? (
          <FormHelperText mt={0} mb={1}>
            {helperText}
          </FormHelperText>
        ) : null}
      </Box>

      <Box gridRow="3" display="flex" alignItems="center" mt={1} w="full">
        <ChakraReactSelect
          inputId={id}
          data-testid={`${testIds.selectInput}-${id}`}
          options={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder={placeholder}
          isDisabled={
            (formControlProps.isDisabled ?? false) || (formControlProps.isReadOnly ?? false)
          }
          colorScheme="brand"
          selectedOptionColorScheme="brand"
          chakraStyles={
            (formControlProps.isReadOnly ?? false) || (formControlProps.isDisabled ?? false)
              ? disabledStyles
              : defaultStyles
          }
          isInvalid={isInvalid}
        />
      </Box>

      <Box gridRow="4" minH={isInvalid ? 'auto' : 0}>
        {isInvalid ? (
          <FormErrorMessage mt={2} mb={0}>
            {error}
          </FormErrorMessage>
        ) : null}
      </Box>
    </FormControl>
  );
}
